<script lang="ts">
  import { link, push } from 'svelte-spa-router';
  import { location } from '../../framework/js/spaLocation';
  import { api } from '../../framework/js/api.js';
  import { AppPageHeader, AgentHero, AppLoadingState } from '../../framework/components/app';
  import BriefBuilderList from './BriefBuilderList.svelte';
  import BriefBuilderView from './BriefBuilderView.svelte';
  import BriefBuilderWizard from './BriefBuilderWizard.svelte';

  let currentId: string | null = null;
  let isNew = false;
  let isContinue = false;
  let hasItems: boolean | null = null;
  let totalCount: number | null = null;

  $: {
    const path = $location || '';
    if (path === '/brief-builder/new') {
      currentId = null;
      isNew = true;
      isContinue = false;
    } else {
      const cont = path.match(/^\/brief-builder\/([^/]+)\/continue$/);
      const view = path.match(/^\/brief-builder\/([^/]+)$/);
      if (cont) {
        currentId = cont[1];
        isNew = false;
        isContinue = true;
      } else if (view && view[1] !== 'new' && view[1] !== 'history') {
        currentId = view[1];
        isNew = false;
        isContinue = false;
      } else {
        currentId = null;
        isNew = false;
        isContinue = false;
      }
    }
  }

  let probeGen = 0;

  async function probe() {
    const gen = ++probeGen;
    try {
      const res: any = await api.listBriefBuilders();
      if (gen !== probeGen) return;
      if (res?.success && Array.isArray(res?.data?.list)) {
        totalCount = res.data.list.length;
        hasItems = res.data.list.length > 0;
      } else {
        hasItems = false;
      }
    } catch {
      if (gen !== probeGen) return;
      hasItems = false;
    }
  }

  function exitWizard() {
    hasItems = null;
    push('/brief-builder');
  }

  // The home stays mounted across /new and /continue, so onMount never
  // runs again when the consultation closes. Load whenever the list is showing.
  $: if (!currentId && !isNew && !isContinue && hasItems === null) {
    probe();
  }
</script>

{#if isNew || isContinue}
  <div class="bb-overlay" role="dialog" aria-modal="true" aria-label="Built-to-Brief consultation">
    <BriefBuilderWizard
      briefId={isContinue ? currentId : null}
      on:close={exitWizard}
    />
  </div>
{:else}
  <div class="bb-page">
    {#if currentId}
      {#key currentId}
        <BriefBuilderView briefId={currentId} />
      {/key}
    {:else if hasItems === null}
      <AppLoadingState label="Loading briefs…" />
    {:else if hasItems}
      <AppPageHeader
        title="Built-to-Brief"
        subtitle="Nine questions. One decision-ready research brief."
        actionLabel="New brief"
        actionHref="/brief-builder/new"
      />
      <BriefBuilderList on:changed={probe} />
    {:else}
      <AgentHero
        title="Built-to-Brief"
        subtitle="Answer the way you'd talk to a researcher across the table. Watch your brief assemble as you go."
        features={[
          'Nine consultative questions in under ten minutes',
          'Live brief assembling beside you as you answer',
          'Decision-ready structure with flagged unknowns',
        ]}
        historyHref="/brief-builder"
        historyLabel="View past briefs"
        historyCount={totalCount}
      >
        <div slot="creator" class="creator-card">
          <h2 class="creator-title">Start a ten-minute consultation</h2>
          <p class="creator-sub">Nine questions. One complete brief.</p>
          <a use:link href="/brief-builder/new" class="creator-cta">
            <span>Begin</span>
            <i class="angle right icon" aria-hidden="true"></i>
          </a>
        </div>
      </AgentHero>
    {/if}
  </div>
{/if}

<style>
  .bb-overlay {
    position: fixed;
    inset: 0;
    z-index: 2000;
    background: var(--dashbg);
  }

  .bb-page {
    padding: var(--space-5) var(--space-6);
  }
  @media (max-width: 768px) {
    .bb-page {
      padding: var(--space-4);
    }
  }

  .creator-card {
    background: var(--surface);
    border: 1px solid var(--border-color);
    border-radius: var(--radius-card);
    box-shadow: var(--shadow-pop);
    padding: var(--space-6);
    text-align: center;
  }

  .creator-title {
    font-size: var(--text-xl);
    font-weight: 500;
    color: var(--grey);
    margin: 0 0 var(--space-2) 0;
  }

  .creator-sub {
    font-size: var(--text-sm);
    color: var(--grey-secondary);
    margin: 0 0 var(--space-5) 0;
  }

  .creator-cta {
    display: inline-flex;
    align-items: center;
    gap: var(--space-2);
    padding: var(--space-3) var(--space-6);
    background: var(--primary);
    color: var(--white);
    border-radius: var(--radius-cta);
    text-decoration: none;
    font-weight: 500;
    box-shadow: var(--shadow-cta);
    transition: filter 0.15s ease;
  }

  .creator-cta:hover {
    filter: brightness(0.95);
  }

  .creator-cta:focus-visible {
    outline: none;
    box-shadow: var(--focus-ring);
  }
</style>
